import React, { memo, useMemo, useEffect, useRef } from 'react';
import { formGroup, formControl } from '@core/utils/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { FormSelect } from '@core/components/form';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@core/components/Button';
import { EventReminder } from '@business/entities/gc_calendar';
import { CardTitle } from 'reactstrap';

const UNIT_OPTIONS: { label: string; value: EventReminder['unit'] }[] = [
  { label: 'Minutes before the event', value: 'minutes' },
  { label: 'Hours before the event', value: 'hours' },
  { label: 'Days before the event', value: 'days' },
  { label: 'Weeks before the event', value: 'weeks' },
];

const VALUE_OPTIONS: Record<EventReminder['unit'], number[]> = {
  minutes: [5, 10, 15, 30, 45],
  hours: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
  days: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
  weeks: [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12],
};

const UNIT_SECONDS: Record<EventReminder['unit'], number> = {
  minutes: 60,
  hours: 3600,
  days: 86400,
  weeks: 604800,
};

function isPastTrigger(startTime: number | undefined, value: number, unit: EventReminder['unit']): boolean {
  if (!startTime) return false;
  const triggerTime = startTime - value * UNIT_SECONDS[unit];
  return triggerTime <= Math.floor(Date.now() / 1000);
}

interface ReminderRowProps {
  reminder: EventReminder;
  startTime?: number;
  onChange: (patch: Partial<EventReminder>) => void;
  onRemove: () => void;
}

const ReminderRow = memo(({ reminder, startTime, onChange, onRemove }: ReminderRowProps) => {
  const form = useMemo(
    () =>
      formGroup({
        value: formControl<number>({ initialValue: reminder.value }),
        unit: formControl<EventReminder['unit']>({ initialValue: reminder.unit }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formValue = useNonNilObservable(form.value$);
  const prevUnitRef = useRef<EventReminder['unit']>(reminder.unit);
  const mountedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (prevUnitRef.current !== formValue.unit) {
      prevUnitRef.current = formValue.unit;
      form.patchValue({ value: VALUE_OPTIONS[formValue.unit][0] });
      return;
    }
    onChangeRef.current({ value: formValue.value, unit: formValue.unit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValue.value, formValue.unit]);

  const valueItems = useMemo(() => VALUE_OPTIONS[formValue.unit].map(v => ({ label: String(v), value: v })), [formValue.unit]);

  const isInPast = useMemo(() => isPastTrigger(startTime, formValue.value, formValue.unit), [startTime, formValue.value, formValue.unit]);

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center" style={{ gap: 6 }}>
        <FormSelect control={form.controls.value} items={valueItems} noLabel formGroupStyle={{ marginBottom: 5, marginTop: 5, minWidth: 150 }} />
        <FormSelect control={form.controls.unit} items={UNIT_OPTIONS} noLabel formGroupStyle={{ marginBottom: 5, marginTop: 5, minWidth: 300 }} />
        <Button onClick={() => onRemove()} intent="danger">
          <i className="fas fa-trash-alt "></i>
        </Button>
      </div>
      {isInPast && (
        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2, paddingLeft: 2 }}>
          <i className="fa fa-exclamation-circle mr-1" />
          This reminder won't be sent — the trigger time is already in the past.
        </div>
      )}
    </div>
  );
});

export interface EventReminderInputProps {
  value: EventReminder[];
  onChange: (reminders: EventReminder[]) => void;
  startTime?: number;
}

export const EventReminderInput = memo(({ value, onChange, startTime }: EventReminderInputProps) => {
  const keysRef = useRef<number[]>([]);
  const nextKeyRef = useRef(0);

  // Grow key list to match value length (handles initial load and edit mode)
  while (keysRef.current.length < value.length) {
    keysRef.current.push(nextKeyRef.current++);
  }
  // Trim when value shrinks externally (e.g. form reset)
  if (keysRef.current.length > value.length) {
    keysRef.current = keysRef.current.slice(0, value.length);
  }

  const addReminder = () => {
    keysRef.current.push(nextKeyRef.current++);
    onChange([...value, { value: 15, unit: 'minutes' }]);
  };

  const removeReminder = (index: number) => {
    keysRef.current.splice(index, 1);
    onChange(value.filter((_, i) => i !== index));
  };

  const updateReminder = (index: number, patch: Partial<EventReminder>) => {
    onChange(value.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <CardTitle>Reminders</CardTitle>
        <Button type="button" onClick={addReminder}>
          <AddIcon style={{ fontSize: 14, marginRight: 2 }} /> Add
        </Button>
      </div>
      {value.map((reminder, index) => (
        <ReminderRow
          key={keysRef.current[index]}
          reminder={reminder}
          startTime={startTime}
          onChange={patch => updateReminder(index, patch)}
          onRemove={() => removeReminder(index)}
        />
      ))}
    </div>
  );
});
